import { Core } from '@walletconnect/core';
import { Buffer } from 'buffer';
import { WalletKit } from '@reown/walletkit';
import { PROJECT_ID_LENGTH } from './Constants';
import { AccountController } from './controllers/AccountController';
import { AnalyticsController } from './controllers/AnalyticsController';
import { NamespaceController } from './controllers/NamespaceController';
import { SessionController } from './controllers/SessionController';
import { StateController } from './controllers/StateController';
import { buildCaipAccounts } from './utils/AccountUtils';
import { extractEvmAddresses } from './utils/EvmUtils';
import { handleRedirectAndClose } from './utils/Redirect';
import { formatRpcRequest } from './utils/RequestUtils';
import { formatRpcResponse } from './utils/ResponseUtils';
globalThis.Buffer = Buffer;
export class WebWalletCore {
    walletKit;
    static instance = null;
    static initPromise = null;
    appKit;
    appKitWalletButton = undefined;
    appKitUpdateEmail = undefined;
    // Controllers
    stateController;
    accountController;
    analyticsController;
    namespaceController;
    sessionController;
    constructor() {
        this.stateController = new StateController();
    }
    // --------------------- Initialization ---------------------
    static async getWallet(options) {
        if (!WebWalletCore.instance) {
            WebWalletCore.instance = new WebWalletCore();
        }
        if (!options) {
            throw new Error('Options are required to initialize wallet');
        }
        if (!options.appKitWalletButton) {
            throw new Error('AppKit wallet button is required to initialize wallet');
        }
        const instance = WebWalletCore.instance;
        if (!options.appkit) {
            throw new Error('AppKit is required to initialize wallet');
        }
        if (instance.getState() === 'UNINITIALIZED') {
            if (!WebWalletCore.initPromise) {
                WebWalletCore.initPromise = instance.init(options);
            }
            await WebWalletCore.initPromise;
        }
        return instance;
    }
    // --------------------- State Management ---------------------
    getState() {
        return this.stateController.getState();
    }
    subscribeState(listener) {
        return this.stateController.subscribeState(listener);
    }
    // --------------------- Core Initialization ---------------------
    async init({ appkit, appKitWalletButton, appKitUpdateEmail, redirectUrl, disconnectOnInit, analyticsUrl }) {
        if (this.getState() !== 'UNINITIALIZED') {
            throw new Error('WebWalletCore already initialized');
        }
        this.appKit = appkit;
        this.appKitWalletButton = appKitWalletButton;
        this.appKitUpdateEmail = appKitUpdateEmail;
        // Initialize controllers
        this.accountController = new AccountController(appkit);
        this.analyticsController = new AnalyticsController(appkit, analyticsUrl);
        this.namespaceController = new NamespaceController(appkit, this.accountController);
        const options = appkit.options;
        const core = new Core({
            projectId: options.projectId,
            customStoragePrefix: 'web-wallet'
        });
        this.walletKit = await WalletKit.init({
            core,
            metadata: {
                name: 'Reown Web Wallet',
                description: 'Reown Web Wallet',
                url: 'https://reown.com/',
                icons: ['https://avatars.githubusercontent.com/u/179229932'],
                ...(redirectUrl && {
                    redirect: {
                        native: redirectUrl
                    }
                })
            },
            signConfig: {
                disableRequestQueue: true
            }
        });
        // Initialize session controller after walletKit is ready
        this.sessionController = new SessionController(this.walletKit, this.appKit);
        if (disconnectOnInit) {
            await this.logout();
        }
        else {
            const accountStatus = await this.accountController.getAccountStatus();
            if (accountStatus === 'connected') {
                this.stateController.setState('READY');
            }
            else {
                this.stateController.setState('IDLE');
            }
        }
    }
    // --------------------- Provider & Account Management ---------------------
    async openUpdateEmailModal(sessionTopic) {
        if (!this.appKitUpdateEmail) {
            throw new Error('AppKit updateEmail is not set');
        }
        const user = await this.sessionController?.getUser();
        if (user?.loginProvider !== 'email') {
            await this.login('email');
        }
        const oldEmail = user?.email;
        const emailUpdateResult = await this.appKitUpdateEmail();
        if (oldEmail && this.sessionController) {
            await this.sessionController.updateUserEmail(oldEmail, emailUpdateResult.email, sessionTopic);
        }
        return emailUpdateResult;
    }
    // --------------------- Session Management ---------------------
    async prepareConnection({ uri, loginProvider, appProjectId }) {
        if (this.getState() === 'UNINITIALIZED') {
            throw new Error('WebWalletCore not initialized');
        }
        if (!this.walletKit)
            throw new Error('WalletKit not initialized');
        if (!this.sessionController)
            throw new Error('SessionController not initialized');
        try {
            const proposalPromise = new Promise((resolve, reject) => {
                // Set a timeout in case the session proposal never arrives
                const timeoutId = setTimeout(() => {
                    reject(new Error('Timed out waiting for session proposal'));
                }, 30000); // 30 second timeout
                this.walletKit.once('session_proposal', proposal => {
                    clearTimeout(timeoutId);
                    resolve(proposal);
                });
            });
            try {
                await this.walletKit.pair({ uri });
            }
            catch (error) {
                throw new Error(`Failed to pair with WalletConnect: ${error instanceof Error ? error.message : String(error)}`);
            }
            const proposal = await proposalPromise;
            const approve = async () => {
                try {
                    if (!this.sessionController || !this.walletKit) {
                        throw new Error('Session controller or wallet kit not initialized');
                    }
                    await this.login(loginProvider);
                    const userInfo = await this.sessionController.getUser();
                    if (!userInfo) {
                        throw new Error('Failed to get user info');
                    }
                    // When possible, override login provider with the one passed in the options
                    // because AppKit often switches auth provider from social login to email
                    if (loginProvider) {
                        userInfo.loginProvider = loginProvider;
                    }
                    const { namespaces, sessionProperties } = await this.namespaceController.buildSessionApprovalResponse(proposal);
                    const session = await this.walletKit.approveSession({
                        id: proposal.id,
                        namespaces,
                        sessionProperties
                    });
                    this.sessionController.saveUserForSession(session.topic, userInfo);
                    if (appProjectId) {
                        this.sendAuthenticatedEvent(appProjectId, userInfo);
                    }
                    return session;
                }
                catch (error) {
                    throw error;
                }
            };
            const reject = async () => {
                await this.walletKit.rejectSession({
                    id: proposal.id,
                    reason: {
                        code: 4001,
                        message: 'User rejected'
                    }
                });
            };
            return {
                proposal,
                proposerMetadata: proposal.params.proposer.metadata,
                verifyContext: proposal.verifyContext,
                approve,
                reject
            };
        }
        catch (error) {
            throw error;
        }
    }
    // Attributes the login to the native app that opened the Web Wallet. The warehouse joins this
    // to Magic's own login log on the EOA address, which is the address Magic records.
    sendAuthenticatedEvent(appProjectId, user) {
        // The session is already approved by this point, so nothing here may throw
        try {
            if (appProjectId.length !== PROJECT_ID_LENGTH) {
                console.warn('[WebWalletCore] App project ID has an unexpected length, skipping event');
                return;
            }
            const account = this.accountController.getAccount();
            const { eoaAddress } = extractEvmAddresses(account);
            if (!eoaAddress) {
                console.warn('[WebWalletCore] No EOA address found, skipping authenticated event');
                return;
            }
            this.analyticsController.sendWebWalletAuthenticated({
                appProjectId,
                address: eoaAddress,
                accounts: buildCaipAccounts(account),
                user
            });
        }
        catch (err) {
            console.error('[WebWalletCore] Failed to send authenticated event:', err);
        }
    }
    async login(loginProvider) {
        if (!this.appKit)
            throw new Error('AppKit not initialized');
        try {
            if (loginProvider && this.appKitWalletButton) {
                await this.appKitWalletButton(loginProvider);
                const status = await this.accountController.getAccountStatus();
                if (status === 'connected') {
                    this.stateController.setState('READY');
                }
                if (status === 'disconnected') {
                    await new Promise(resolve => setTimeout(resolve, 5000));
                    const status = await this.accountController.getAccountStatus();
                    if (status === 'connected') {
                        this.stateController.setState('READY');
                    }
                    if (status === 'disconnected') {
                        throw new Error('Account is still disconnected');
                    }
                }
            }
            else {
                await this.appKit.open();
                // Wait for account to be connected
                await new Promise(resolve => {
                    this.appKit?.subscribeAccount(state => {
                        if (state.status === 'connected' && state.embeddedWalletInfo) {
                            resolve(true);
                        }
                    });
                });
                this.stateController.setState('READY');
            }
        }
        catch (error) {
            throw error;
        }
    }
    async logout() {
        if (!this.appKit)
            throw new Error('AppKit not initialized');
        await this.appKit.disconnect();
        await new Promise(resolve => setTimeout(resolve, 2000));
        // Clear localStorage keys to _reliably_ disconnect from AppKit
        const keysToRemove = [
            '@appkit-wallet/EMAIL',
            '@appkit-wallet/EMAIL_LOGIN_USED_KEY',
            // '@appkit-wallet/LAST_USED_CHAIN_KEY',
            // '@appkit-wallet/SMART_ACCOUNT_ENABLED_NETWORKS',
            // '@appkit/active_caip_network_id',
            // '@appkit/active_namespace',
            // '@appkit/connected_namespaces',
            '@appkit/connection_status',
            '@appkit/connections',
            '@appkit/disconnected_connector_ids',
            '@appkit/eip155:connected_connector_id',
            // // '@appkit/identity_cache',
            // // '@appkit/native_balance_cache',
            // // '@appkit/portfolio_cache',
            '@appkit/solana:connected_connector_id'
            // 'wagmi.recentConnectorId'
        ];
        keysToRemove.forEach(key => {
            localStorage.removeItem(key);
        });
        this.stateController.setState('IDLE');
    }
    // --------------------- Request Handling ---------------------
    async getPendingRequests() {
        if (!this.sessionController)
            throw new Error('SessionController not initialized');
        const requests = await this.sessionController.getPendingRequests();
        return requests;
    }
    async getCurrentUser() {
        if (!this.sessionController)
            throw new Error('SessionController not initialized');
        return this.sessionController.getUser();
    }
    async processRequest(request, redirect) {
        if (!this.sessionController)
            throw new Error('SessionController not initialized');
        try {
            await this.accountController.ensureCorrectNetwork(request.params.chainId);
            await this.accountController.ensureCorrectAccount(request);
            const provider = await this.accountController.getProvider();
            const formattedRequest = formatRpcRequest(request);
            const response = await provider.request(formattedRequest);
            const formattedResponse = formatRpcResponse(response, formattedRequest.method);
            await this.sessionController.respondToRequest(request, { result: formattedResponse });
        }
        catch (err) {
            await this.sessionController.respondToRequest(request, { error: err });
        }
        if (redirect && typeof window !== 'undefined') {
            const sessions = this.walletKit?.getActiveSessions();
            if (sessions && request.topic) {
                handleRedirectAndClose(sessions[request.topic].peer.metadata, this.walletKit);
            }
        }
    }
    subscribeToSessionRequests(callback) {
        if (!this.sessionController)
            throw new Error('SessionController not initialized');
        if (!this.walletKit)
            throw new Error('WalletKit not initialized');
        // Set up event listener for new requests
        const unsubscribe = this.sessionController.subscribeToSessionRequests(callback);
        // Check for any pending requests and send the oldest one if exists
        const pendingRequests = this.walletKit.getPendingSessionRequests();
        if (pendingRequests.length > 0) {
            const oldestRequest = pendingRequests[0];
            const user = this.sessionController.getUserForSession(oldestRequest.topic);
            if (user) {
                callback({ ...oldestRequest, user });
            }
        }
        return unsubscribe;
    }
}
//# sourceMappingURL=WebWalletCore.js.map